import React, { useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BadgeCheck, AlertTriangle, Clock, Loader2, Search, Ban, ShieldCheck } from 'lucide-react';
import CpscTable, { CPSC_TH_CLASS } from './CpscTable';

/**
 * CpscStatusChecker — CHA-179. Tenant-admin test/lookup surface for the CPSC status resolution engine
 * (`getCpscStatusForStyles`). Paste production styles → per style: does CPSC apply (HTS-driven) and do we
 * have a certificate (auto-resolved — direct Target-style match OR GMS-prefix, no manual mapping, no UPC).
 * Read-only, admin-gated, STANDALONE — does NOT touch the daily-ops dashboards (badges on those are a
 * separate consumer PR). This is the surface to validate the engine's answers in the browser first.
 */
const s = (v) => String(v ?? '').trim();

// Engine badge status → display. Engine returns: certified | awaiting | submitted | failed | in_progress | none | na | exempt.
const BADGE = {
  certified: { tone: 'green', icon: BadgeCheck, label: 'Certified' },
  awaiting: { tone: 'green', icon: Clock, label: 'Awaiting cert' },
  submitted: { tone: 'blue', icon: Clock, label: 'Submitting' },
  in_progress: { tone: 'blue', icon: Clock, label: 'In progress' },
  failed: { tone: 'red', icon: AlertTriangle, label: 'Failed' },
  none: { tone: 'amber', icon: AlertTriangle, label: 'Missing' },
  na: { tone: 'slate', icon: Ban, label: 'N/A' },
  exempt: { tone: 'teal', icon: ShieldCheck, label: 'Exempt' }, // CHA-222 — 16 CFR 1610.1(d) flammability exemption; no cert required
  not_subject: { tone: 'slate', icon: Ban, label: 'Not subject' }, // CHA-540 — Target TTS determination, not a 1610 exemption
};
const TONE = {
  green: 'text-carbon-green-50 border-carbon-green-50 bg-carbon-green-10', amber: 'text-carbon-orange-70 border-carbon-orange-40 bg-carbon-white',
  red: 'text-carbon-red-60 border-carbon-red-60 bg-carbon-white', blue: 'text-carbon-blue-60 border-carbon-blue-60 bg-carbon-blue-10',
  slate: 'text-carbon-gray-70 border-carbon-gray-30 bg-carbon-white',
  teal: 'text-carbon-teal-60 border-carbon-teal-40 bg-carbon-white', // CHA-222 — distinct from certified-green + needs-cert-amber/red
};

// Split pasted input into styles: newline / comma / semicolon / whitespace separated; dedupe; drop blanks.
const parseStyles = (text) => [...new Set(String(text || '').split(/[\s,;]+/).map(s).filter(Boolean))];

export default function CpscStatusChecker({ userProfile }) {
  const role = userProfile?.role;
  const isAdmin = role === 'platform_owner' || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [input, setInput] = useState('');
  const [rows, setRows] = useState(null);   // [{ style, status, cpscApplicable, certType, sourceChain }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    const styleNos = parseStyles(input);
    if (!styleNos.length) { setError('Enter one or more production styles.'); return; }
    setLoading(true); setError(null); setRows(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getCpscStatusForStyles', { timeout: 150000 }); // CHA-382 / CHA-528 — server's 120s window + 30s transport headroom
      const res = await fn({ styleNos, tenantId });
      const statuses = (res && res.data && res.data.statuses) || {};
      setRows(styleNos.map((style) => ({ style, ...(statuses[style] || { status: 'none' }) })));
    } catch (e) {
      console.error('CPSC status check failed', e); // don't swallow (§8.3)
      setError(e && e.message ? e.message : 'Check failed. Confirm access and retry.');
    } finally { setLoading(false); }
  }, [input, tenantId]);

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        CPSC Status Checker is restricted to administrators.
      </div>
    );
  }

  const pending = parseStyles(input).length;
  const counts = rows ? rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {}) : null;

  return (
    <div className="font-plex text-carbon-ink space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-carbon-ink flex items-center gap-2"><Search className="h-5 w-5" /> CPSC Status Checker</h2>
        <p className="text-sm text-carbon-gray-70 mt-1">
          Paste production styles (one per line, or comma/space separated). Each is checked for CPSC
          applicability (from its HTS) and certificate status — resolved automatically, no manual mapping.
        </p>
      </div>

      <textarea
        className="w-full h-28 border border-carbon-gray-30 bg-carbon-gray-10 px-3 py-2 text-sm font-plex-mono focus:outline-none focus:ring-2 focus:ring-carbon-blue-60"
        placeholder={'e.g.\nAII82B\nAII81D\nWG19750'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        aria-label="Production styles to check"
      />

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={check}
          disabled={loading || !pending}
          className="inline-flex items-center gap-2 bg-carbon-blue-60 text-white px-4 py-2 text-sm font-semibold hover:bg-carbon-blue-70 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Check{pending ? ` (${pending})` : ''}
        </button>
        {counts && (
          <div className="font-plex-mono text-[11px] text-carbon-gray-70 flex flex-wrap gap-3">
            {Object.entries(counts).map(([st, n]) => <span key={st}>{(BADGE[st] && BADGE[st].label) || st}: <b>{n}</b></span>)}
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="bg-carbon-white border border-carbon-border overflow-x-auto">
          <CpscTable
            headClassName={CPSC_TH_CLASS}
            columns={[
              { label: 'Style', className: 'text-left px-3 py-2' },
              { label: 'CPSC Status', className: 'text-left px-3 py-2' },
              { label: 'Needs CPSC?', className: 'text-left px-3 py-2' },
              { label: 'HTS', className: 'text-left px-3 py-2' },
              { label: 'Resolved via', className: 'text-left px-3 py-2' },
            ]}
          >
              {rows.map((r) => {
                const b = BADGE[r.status] || BADGE.none;
                const Icon = b.icon;
                const src = r.sourceChain || {};
                // CHA-222: an exempt style needs NO certificate — don't show "Yes (GCC)" for it.
                const needs = r.status === 'not_subject' ? 'No (Target TTS)'
                  : r.status === 'exempt' ? `No (exempt${r.exemptionPath ? ` — Path ${r.exemptionPath}` : ''})`
                  : r.cpscApplicable === true ? `Yes${r.certType ? ` (${r.certType})` : ''}` : r.cpscApplicable === false ? 'No' : 'Unknown';
                const via = r.status === 'not_subject' ? 'Target TTS'
                  : r.status === 'exempt' ? `exemption${r.exemptionPath ? ` (Path ${r.exemptionPath})` : ''}`
                  : src.matchedVia === 'gms-prefix' ? `GMS ${src.gms || ''}` : src.matchedVia === 'direct' ? 'direct' : '—';
                return (
                  <tr key={r.style} className="border-t border-carbon-gray-10">
                    <td className="px-3 py-2 font-plex-mono">{r.style}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-plex-mono text-[10.5px] uppercase font-medium ${TONE[b.tone]}`}>
                        <Icon className="h-3.5 w-3.5" /> {b.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-carbon-gray-70">{needs}</td>
                    <td className="px-3 py-2 font-plex-mono text-carbon-gray-50">{src.hts || '—'}</td>
                    <td className="px-3 py-2 text-carbon-gray-50 text-xs">{via}</td>
                  </tr>
                );
              })}
          </CpscTable>
        </div>
      )}
      {rows && rows.length === 0 && <div className="text-sm text-carbon-gray-70">No results.</div>}
    </div>
  );
}
